const CYRILLIC_TO_LATIN: Record<string, string> = {
  А: "A", В: "B", Е: "E", К: "K", М: "M", Н: "H",
  О: "O", Р: "P", С: "C", Т: "T", У: "Y", Х: "X",
}

const LATIN_TO_CYRILLIC: Record<string, string> = {
  A: "А", B: "В", E: "Е", K: "К", M: "М", H: "Н",
  O: "О", P: "Р", C: "С", T: "Т", Y: "У", X: "Х",
}

export const ALLOWED_RU_PLATE_LETTERS = "ABEKMHOPCTYX"
export const DEMO_PLATE_NORMALIZED = "A000AA000"
const RU_PLATE_RE = /^[ABEKMHOPCTYX]\d{3}[ABEKMHOPCTYX]{2}\d{2,3}$/
const SEPARATORS = new Set([" ", "-", ".", "_", "\t", "\n", "\r"])

export const RUSSIAN_PLATE_000_ERROR =
  "Номера с основной частью 000 недоступны в BeepApp."

function toLatinUpper(char: string): string {
  const upper = char.toUpperCase()
  return CYRILLIC_TO_LATIN[upper] ?? upper
}

function isAllowedPlateLetter(char: string): boolean {
  return ALLOWED_RU_PLATE_LETTERS.includes(char)
}

function isDigit(char: string): boolean {
  return char >= "0" && char <= "9"
}

function isReservedZeroNumber(value: string): boolean {
  const normalized = value.toUpperCase()
  if (normalized === DEMO_PLATE_NORMALIZED) return false
  return normalized.length >= 4 && normalized.slice(1, 4) === "000"
}

function matchesPlatePosition(char: string, position: number): boolean {
  if (position === 0 || position === 4 || position === 5) {
    return isAllowedPlateLetter(char)
  }
  if (position >= 1 && position <= 3) {
    return isDigit(char)
  }
  if (position >= 6 && position <= 8) {
    return isDigit(char)
  }
  return false
}

/**
 * Жёсткая маска ввода российского номера.
 *
 * Правило: буква + 3 цифры + 2 буквы + регион 2/3 цифры.
 * Недопустимый символ не попадает в состояние input, поэтому визуально
 * он «не появляется» в поле ввода: цифра не встанет на место буквы,
 * буква не встанет на место цифры, лишний 10-й символ отбрасывается.
 *
 * Основная трёхзначная часть 000 запрещена, кроме канонического демо-номера
 * A000AA000. Маска разрешает набрать его префикс A000; окончательная проверка
 * всё равно отклонит любой другой номер 000-серии.
 */
export function applyRussianPlateInputMask(value: string): string {
  const result: string[] = []

  for (const rawChar of value) {
    if (SEPARATORS.has(rawChar)) {
      continue
    }

    const char = toLatinUpper(rawChar)
    const position = result.length

    if (position >= 9) {
      break
    }

    if (
      position === 3 &&
      result[1] === "0" &&
      result[2] === "0" &&
      char === "0" &&
      result[0] !== "A"
    ) {
      continue
    }

    if (matchesPlatePosition(char, position)) {
      result.push(char)
    }
  }

  return result.join("")
}

/**
 * Нормализация для API/проверок. Оставлена как отдельное имя,
 * чтобы остальной код мог вызывать normalizePlateInput().
 */
export function normalizePlateInput(value: string): string {
  return applyRussianPlateInputMask(value)
}

export function isBlockedRussianPlate(value: string): boolean {
  return isReservedZeroNumber(normalizePlateInput(value))
}

export function isValidRussianPlate(value: string): boolean {
  const normalized = normalizePlateInput(value)
  return RU_PLATE_RE.test(normalized) && !isReservedZeroNumber(normalized)
}

export function russianPlateToCyrillic(value: string): string {
  const normalized = normalizePlateInput(value)
  return Array.from(normalized, (char) => LATIN_TO_CYRILLIC[char] ?? char).join("")
}

export function formatPlateForDisplay(value: string): string {
  const normalized = russianPlateToCyrillic(value)
  if (normalized.length <= 1) return normalized
  if (normalized.length <= 4) return `${normalized[0]} ${normalized.slice(1)}`
  if (normalized.length <= 6) return `${normalized[0]} ${normalized.slice(1, 4)} ${normalized.slice(4)}`
  return `${normalized[0]} ${normalized.slice(1, 4)} ${normalized.slice(4, 6)} ${normalized.slice(6)}`
}

export const RUSSIAN_PLATE_ERROR =
  "Формат номера РФ: А 777 АА 77 или А 777 АА 777. Допустимые буквы: А, В, Е, К, М, Н, О, Р, С, Т, У, Х."
